import { put, call, select, takeLatest } from 'redux-saga/effects';
import { notification } from 'antd';
import { push } from 'connected-react-router';
import FileSaver from 'file-saver';
import ApiService from '../Services/ApiService';
import AppActions from '../Stores/App/Actions';
import GdsRcpt0101Actions, { GdsRcpt0101Types } from '../Stores/GdsRcpt0101/Actions';

const getAppStore = state => state.app;

export function* gdsRcpt0101FetchGdsRcpt0101({ currentPage, sorts, filters, pageSize }) {
  try {
    yield put(GdsRcpt0101Actions.gdsRcpt0101FetchGdsRcpt0101Loading(true));

    const processedSorts = [];
    Object.entries(sorts).forEach(entry => {
      const key = entry[0];
      const value = entry[1];
      processedSorts.push(`${key}:${value === 'ascend' ? 'ASC' : 'DESC'}`);
    });

    const processedFilters = [];
    Object.entries(filters).forEach(entry => {
      const key = entry[0];
      const value = entry[1];
      if (value) {
        processedFilters.push(`${key}:${value}`);
      }
    });

    const app = yield select(getAppStore);
    const getData = {
      page: currentPage,
      sorts: processedSorts,
      filters: processedFilters,
      pageSize
    };

    const result = yield call(
      ApiService.getApi, // function
      app.apiUrl,
      `gdsRcpt/indexProcess/GDS_RCPT_01_01/${app.curSiteFlowId}`,
      app.token,
      getData,
      'multipart/form-data' // params
    );

    if (result.isSuccess === true) {
      // if nowCurrentPage is more than lastPage, then nowCurrentPage = lastPage
      let nowCurrentPage = result.data.current_page;
      if (nowCurrentPage > result.data.last_page) {
        nowCurrentPage = result.data.last_page;
      }

      yield put(
        GdsRcpt0101Actions.gdsRcpt0101FetchGdsRcpt0101Success(
          result.data.data,
          nowCurrentPage,
          result.data.last_page,
          result.data.total,
          result.data.per_page
        )
      );
    } else if (result.isTokenExpired === true) {
      yield put(AppActions.appTokenExpired(result.message));
    } else if (result.isPasswordExpired === true) {
      yield put(AppActions.appPasswordExpired(result.message));
    } else {
      yield call(notification.error, {
        message: result.message,
        duration: parseInt(process.env.REACT_APP_ERROR_MESSAGE_DURATION, 10)
      });
    }
  } catch (error) {
    yield call(notification.error, {
      message: error.message,
      duration: parseInt(process.env.REACT_APP_ERROR_MESSAGE_DURATION, 10)
    });
  } finally {
    yield put(GdsRcpt0101Actions.gdsRcpt0101FetchGdsRcpt0101Loading(false));
  }
}

export function* gdsRcpt0101PrintGdsRcpt0101({ hdrIds }) {
  try {
    yield put(GdsRcpt0101Actions.gdsRcpt0101PrintGdsRcpt0101Loading(true));

    const app = yield select(getAppStore);

    const getData = {
      hdrIds
    };

    const result = yield call(
      ApiService.downloadGetApi, // function
      app.apiUrl,
      `gdsRcpt/printProcess/GDS_RCPT_01_01/${app.curSiteFlowId}`,
      app.token,
      getData,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );

    if (result.isSuccess === true) {
      const selectedDocuments = hdrIds.map(d => ({ id: d }));

      FileSaver.saveAs(result.data, 'GdsRcpt.pdf');
      yield put(GdsRcpt0101Actions.gdsRcpt0101RemoveSelectedDocuments(selectedDocuments));
      yield put(GdsRcpt0101Actions.gdsRcpt0101ResetTimestamp());
    } else if (result.isTokenExpired === true) {
      yield put(AppActions.appTokenExpired(result.message));
    } else if (result.isPasswordExpired === true) {
      yield put(AppActions.appPasswordExpired(result.message));
    } else {
      yield call(notification.error, {
        message: result.message,
        duration: parseInt(process.env.REACT_APP_ERROR_MESSAGE_DURATION, 10)
      });
    }
  } catch (error) {
    yield call(notification.error, {
      message: error.message,
      duration: parseInt(process.env.REACT_APP_ERROR_MESSAGE_DURATION, 10)
    });
  } finally {
    yield put(GdsRcpt0101Actions.gdsRcpt0101PrintGdsRcpt0101Loading(false));
  }
}

export function* gdsRcpt0101GoToDocument({ hdrId }) {
  const app = yield select(getAppStore);
  yield put(push(`${app.appPath}/gdsRcptDetail/update/${hdrId}`));
}

export function* gdsRcpt0101PrintHandlingUnit({ hdrIds }) {
  try {
    yield put(GdsRcpt0101Actions.gdsRcpt0101PrintGdsRcpt0101Loading(true));

    const app = yield select(getAppStore);

    const getData = {
      ids: hdrIds
    };

    const result = yield call(
      ApiService.downloadGetApi, // function
      app.apiUrl,
      `handlingUnit/printProcess/HANDLING_UNIT_LIST_04/${app.curSiteFlowId}`,
      app.token,
      getData,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );

    if (result.isSuccess === true) {
      const selectedDocuments = hdrIds.map(d => ({ id: d }));

      FileSaver.saveAs(result.data, 'PalletIDs.pdf');
      yield put(GdsRcpt0101Actions.gdsRcpt0101RemoveSelectedDocuments(selectedDocuments));
      yield put(GdsRcpt0101Actions.gdsRcpt0101ResetTimestamp());
    } else if (result.isTokenExpired === true) {
      yield put(AppActions.appTokenExpired(result.message));
    } else if (result.isPasswordExpired === true) {
      yield put(AppActions.appPasswordExpired(result.message));
    } else {
      yield call(notification.error, {
        message: result.message,
        duration: parseInt(process.env.REACT_APP_ERROR_MESSAGE_DURATION, 10)
      });
    }
  } catch (error) {
    yield call(notification.error, {
      message: error.message,
      duration: parseInt(process.env.REACT_APP_ERROR_MESSAGE_DURATION, 10)
    });
  } finally {
    yield put(GdsRcpt0101Actions.gdsRcpt0101PrintGdsRcpt0101Loading(false));
  }
}

export const saga = [
  takeLatest(GdsRcpt0101Types.GDS_RCPT0101_GO_TO_DOCUMENT, gdsRcpt0101GoToDocument),
  takeLatest(GdsRcpt0101Types.GDS_RCPT0101_FETCH_GDS_RCPT0101, gdsRcpt0101FetchGdsRcpt0101),
  takeLatest(GdsRcpt0101Types.GDS_RCPT0101_PRINT_GDS_RCPT0101, gdsRcpt0101PrintGdsRcpt0101),
  takeLatest(GdsRcpt0101Types.GDS_RCPT0101_PRINT_HANDLING_UNIT, gdsRcpt0101PrintHandlingUnit)
];
